import ButtonFFXIV from '@/app/components/ButtonFFXIV'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from '@/hooks/useTranslation'
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { GameMode } from '@/lib/types/common/GameMode';
import { isTodayDailyCompleted } from '@/lib/utils/dailyUtils';

const GameModeConfigActions = () => {
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();

    const isGameMaster = room?.sessionId === gameState?.gameMasterId;
    const isFightMode = gameState?.gameMode === GameMode.FIGHT ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.FIGHT);
    const isCoopMode = gameState?.gameMode === GameMode.COOP ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.COOP);
    const isTeamMode = isFightMode || isCoopMode;
    
    // Daily mode doesn't need expansion/map type selection (uses seed)
    const isDailyMode = gameState?.gameMode === GameMode.DAILY;
    
    // Convert ArraySchema to arrays for proper tracking
    const selectedExpansions = Array.from(gameState?.selectedExpansions || []);
    const selectedMapTypes = Array.from(gameState?.selectedMapTypes || []);
    
    // Expansion and map type validation (not needed for Daily mode)
    const selectionValidation = useMemo(() => {
        // Daily mode gets expansions/map types from seed, skip validation
        if (isDailyMode) {
            return { isValid: true, message: null };
        }
        
        const hasExpansions = selectedExpansions.length > 0;
        const hasMapTypes = selectedMapTypes.length > 0;
        
        if (!hasExpansions && !hasMapTypes) {
            return {
                isValid: false,
                message: t('game.config.validation.noExpansionsAndMapTypes')
            };
        }
        
        if (!hasExpansions) {
            return {
                isValid: false,
                message: t('game.config.validation.noExpansions')
            };
        }
        
        if (!hasMapTypes) {
            return {
                isValid: false,
                message: t('game.config.validation.noMapTypes')
            };
        }
        
        return { isValid: true, message: null };
    }, [selectedExpansions.length, selectedMapTypes.length, isDailyMode, t]);

    // Team mode validation (Fight and Coop)
    const teamModeValidation = useMemo(() => {
        if (!isTeamMode || !gameState?.teams) {
            return { canStart: true, message: null };
        }

        // Check if all players are in a team
        const playersWithoutTeam: string[] = [];
        gameState.players?.forEach((player) => {
            if (!player.teamId || player.teamId === '') {
                playersWithoutTeam.push(player.name);
            }
        });
        console.log('Players without team:', playersWithoutTeam);
        if (playersWithoutTeam.length > 0) {
            return {
                canStart: false,
                message: t('play.fight.playersNeedTeam', { count: playersWithoutTeam.length })
            };
        }

        // Check if each team has at least one player
        const emptyTeams: string[] = [];
        gameState.teams.forEach(team => {
            const memberCount = team.memberSessionIds?.length || 0;
            console.log(`Team ${team.name} has ${memberCount} members.`);
            if (memberCount === 0) {
                emptyTeams.push(team.name);
            }
        });

        if (emptyTeams.length > 0) {
            return {
                canStart: false,
                message: t('play.fight.teamsNeedPlayers', { count: emptyTeams.length })
            };
        }

        return { canStart: true, message: null };
    }, [gameState, isTeamMode, gameState?.players, gameState?.teams, t]);

    // Listen for game start errors from server
    useEffect(() => {
        if (!room) return;

        const handleGameStartError = (data: { error: string; message: string }) => {
            showPopup({
                type: 'error',
                message: data.message,
            });
        };

        room.onMessage("gameStartError", handleGameStartError);

        return () => {
            room.onMessage("gameStartError", () => {});
        };
    }, [room, showPopup]);

    const handleStartGame = () => {
        if (!isGameMaster || !room) {
            showPopup({
                type: 'error',
                message: t('lobby.onlyGameMasterCan'),
            });
            return;
        }
        
        // Check if daily has already been completed
        if (isDailyMode && isTodayDailyCompleted()) {
            showPopup({
                type: 'error',
                message: t('game.modes.daily.alreadyCompleted'),
            });
            return;
        }
        
        // Client-side validation for expansion/map type selection
        if (!selectionValidation.isValid) {
            showPopup({
                type: 'error',
                message: selectionValidation.message || t('game.config.validation.noExpansionsAndMapTypes'),
            });
            return;
        }

        // Client-side validation for Team modes (Fight/Coop)
        if (isTeamMode && !teamModeValidation.canStart) {
            showPopup({
                type: 'error',
                message: teamModeValidation.message || 'Cannot start game',
            });
            return;
        }
        
        // Send message to server to start the game
        room.send("startGame");
    }

    const canStart = isGameMaster && selectionValidation.isValid && (!isTeamMode || teamModeValidation.canStart) && (!isDailyMode || !isTodayDailyCompleted());

    if (!room || !gameState) return null;

    return (
        <div className="bg-[#323232] flex justify-center items-center sticky bottom-0 pb-[9px] translate-y-[17px] z-[100]">
            <ButtonFFXIV
                text={t('lobby.start')}
                onClick={handleStartGame}
                disabled={!canStart}
                fitContent={true}
            />
        </div>
    )
}

export default GameModeConfigActions